import cn from "classnames";
import { type ContentTab, ContentView } from "components";
import { type Node } from "components/charts/types/Sankey";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { selectFlowGoalPerformanceInfo } from "../../state/slices/flowGoalSlice";
import { isAllowedGoal } from "../../state/slices/flowPerformanceSlice";
import FlowApplicationUsage from "./FlowApplicationUsage";
import FlowEngagement from "./FlowEngagement";
import FlowPrioritization from "./FlowPrioritization";
import styles from "./FlowPerformanceTabs.module.scss";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import NotifyUsersModal from "features/Library/Flows/Edit/NotifyUsersModal/NotifyUsersModal";
import { bindActionCreators } from "redux";
import { sendTransientNotification as sendTransientNotificationAction } from "features/Notifications/state/notificationsActions";
import { useCallback, useMemo } from "react";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { Button } from "components/buttons/button/Button";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { type FlowPriorityModal } from "../../types/performance";
import { PriorityLevels } from "enums";

export interface FlowPerformanceTabsProps extends PropsFromRedux {
  dateFilter: PerformanceWithAccountFilter | null;
  onClick: (node: Node) => void;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  activeTab: number;
  setActiveTab: (tab: number) => void;
  flowId: number;
  canNotify?: boolean;
  appliedStatus?: number[];
}

const LABELS = {
  flowEngagement: "Flow Engagement",
  flowPrioritization: "Flow Prioritization",
  applicationUsage: "Application Usage",
};

export const FlowPerformanceTabs = ({
  dateFilter,
  goalInfo,
  onClick,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
  activeTab,
  setActiveTab,
  flowId,
  sendTransientNotification,
  canNotify,
  appliedStatus,
}: FlowPerformanceTabsProps) => {
  const goalAllowed = isAllowedGoal(goalInfo!);
  const msGraphGoalReporting = useFeatureFlag(FeatureFlags.MsGraphGoalReporting);
  const notifyUsersFeature = useFeatureFlag(FeatureFlags.NotifyUsers);

  const handleNotifySuccess = useCallback(() => {
    sendTransientNotification({ title: "Success!", message: "Email sent successfully.", type: 0 }, 5);
  }, [sendTransientNotification]);

  const tabs = useMemo<ContentTab[]>(() => {
    if (!dateFilter) return [];
    const returnVal = [
      {
        label: LABELS.flowPrioritization,
        component: (
          <FlowPrioritization
            flowId={flowId}
            dateFilter={dateFilter}
            includeAccounts={includeAccounts}
            childAccountId={childAccountId}
            hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          />
        ),
      },
      {
        label: LABELS.flowEngagement,
        component: <FlowEngagement includeAccounts={includeAccounts} dateFilter={dateFilter} onClick={onClick} />,
      },
    ];

    if (goalAllowed && msGraphGoalReporting) {
      returnVal.splice(1, 0, {
        label: LABELS.applicationUsage,
        component: <FlowApplicationUsage />,
      });
    }

    return returnVal;
  }, [
    childAccountId,
    dateFilter,
    flowId,
    goalAllowed,
    hasCreateAccountsAddOn,
    includeAccounts,
    msGraphGoalReporting,
    onClick,
  ]);

  // Improve: Move this into the useMemo above once better testing can be done

  const flowIds = useMemo<FlowPriorityModal[]>(() => {
    let flowPriority = [PriorityLevels.recommended, PriorityLevels.important, PriorityLevels.required];
    if (dateFilter && dateFilter.priority && dateFilter.priority.length > 0) {
      flowPriority = dateFilter.priority;
    }
    return flowPriority.map((priority) => ({ FlowId: flowId, FlowPriority: priority }));
  }, [flowId, dateFilter]);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      className={cn("sankeySection", styles.flowReportTabs, {
        noMax: tabs[activeTab]?.label !== LABELS.flowEngagement,
      })}
    >
      <ContentView
        activeIndex={activeTab}
        onTabChange={(_e, { activeIndex }) => setActiveTab(activeIndex)}
        tabs={tabs}
      />

      {notifyUsersFeature && canNotify && tabs[activeTab]?.label === LABELS.flowPrioritization && (
        <NotifyUsersModal
          flowInfos={flowIds}
          onNotifySuccess={handleNotifySuccess}
          appliedStatuses={appliedStatus}
          renderTrigger={(onOpen) => (
            <Button
              icon={faEnvelope}
              size={"medium"}
              basic
              borderless
              className={styles.notifyButton}
              color={"blue"}
              onClick={onOpen}
            >
              Notify Users
            </Button>
          )}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  goalInfo: selectFlowGoalPerformanceInfo(state),
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  sendTransientNotification: bindActionCreators(sendTransientNotificationAction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(FlowPerformanceTabs);
export default ConnectedComponent;
